import { useCallback, useContext, useState } from "react";
import { UserContext } from "../../../context/user/UserContext";
import {
  forgotPasswordService,
  signInService,
  signUpService,
  resetPasswordService,
  verifyEmailService,
  resendVerificationService,
} from "../services/auth.service";

export const useAuth = () => {
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState([]);
  const [error, setError] = useState(null);
  const { setUser } = useContext(UserContext);

  // Helper to handle async operations and standardize errors
  const withAuthHandling = useCallback(async (action, options = { clearFields: true }) => {
    setLoading(true);
    setError(null);
    if (options.clearFields) setFieldErrors([]);

    try {
      const data = await action();
      return data;
    } catch (err) {
      if (err.code === "VALIDATION_ERROR") {
        setFieldErrors(err.errors || []);
      } else {
        setError(err.message || "Something went wrong. Please try again.");
      }
      throw err; // Re-throw to let the UI component handle specific side-effects (like toasts)
    } finally {
      setLoading(false);
    }
  }, []);

  const signIn = useCallback(
    async (email, password) => {
      return withAuthHandling(async () => {
        const data = await signInService(email, password);
        setUser(data.user);
        return data;
      });
    },
    [setUser, withAuthHandling]
  );

  const signUp = useCallback(
    async (userData) => {
      return withAuthHandling(async () => {
        const data = await signUpService(userData);
        setUser(data.user);
        return data;
      });
    },
    [setUser, withAuthHandling]
  );

  const forgotPassword = useCallback(async (email) => {
    return withAuthHandling(async () => {
      return await forgotPasswordService(email);
    });
  }, [withAuthHandling]);

  const resetPassword = useCallback(async (token, password) => {
    return withAuthHandling(async () => {
      return await resetPasswordService(token, password);
    });
  }, [withAuthHandling]);

  const verifyEmail = useCallback(async (token) => {
    return withAuthHandling(async () => {
      const data = await verifyEmailService(token);
      if (data.user) {
        setUser(prev => ({ ...prev, ...data.user }));
      }
      return data;
    });
  }, [setUser, withAuthHandling]);

  const resendVerification = useCallback(async () => {
    return withAuthHandling(async () => {
      const data = await resendVerificationService();
      if (data.verification_email_count !== undefined) {
        setUser(prev => ({ 
          ...prev, 
          verification_email_count: data.verification_email_count,
          last_verification_attempt: data.last_verification_attempt || new Date()
        }));
      }
      return data;
    });
  }, [setUser, withAuthHandling]);

  const clearAuthErrors = useCallback(() => {
    setError(null);
    setFieldErrors([]);
  }, []);

  return {
    loading,
    error,
    fieldErrors,
    signIn,
    signUp,
    forgotPassword,
    resetPassword,
    verifyEmail,
    resendVerification,
    clearAuthErrors,
    setFieldErrors,
    setError,
  };
};
